import React, { useState, useEffect } from "react";
import { Autocomplete } from "@mui/material";
import { TextField } from '@mui/material';

import { genericSearchSuggestions } from "../Api/DigitalTwinListApi";

export default function DeviceSearchInput({
    onSelect,
    onExecuteSearch,
    disableSuggestions = false,
    columnName = "deviceID",
    container,
    searchSuggestionsHandler = genericSearchSuggestions, // Default to imported function if not provided
    // --- form-field mode -------------------------------------------------------------
    // The defaults below all reproduce the original search-box behaviour, so the three
    // existing call sites (TableSearch, DigitalTwinTableSearch, MapDigitalTwinSearch) are
    // unaffected. Pass `value` to switch into a controlled form field: the input keeps its
    // value after a suggestion is picked instead of clearing, which is what a form needs
    // and a search box does not.
    value = null,
    onValueChange = null,
    onSuggestionsChange = null,
    autoFocus = true,
    error = false,
    suggestionLimit = 5,
    // Characters required before the dropdown opens. Defaults to 3, preserving the original
    // `value.length > 2` behaviour; a caller whose backend starts returning matches at 2
    // should pass 2, otherwise it fetches results it never shows.
    minCharsToOpen = 3,
    inputSx = null,
    inputProps = null,
    ...props
}) {
    const batteryID = ""
    const [batteryList, setBatteryList] = useState([]);
    const [typedValue, setTypedValue] = useState("");
    const [open, setOpen] = React.useState(false);
    const [errorTextfield, setErrorTextField] = React.useState(false);

    const isControlled = value !== null;
    // One search term regardless of mode, so the suggestion fetch below has a single source.
    const searchTerm = isControlled ? (value || "") : typedValue;

    // Dependencies are deliberately the original two (see the disable at the dep array).
    // columnName is excluded to keep the three existing search boxes behaving as they do
    // today — they don't refetch when the search column changes. The handler props are
    // excluded because a parent naturally passes fresh closures, which would refetch on
    // every render.
    useEffect(() => {
        if (disableSuggestions || !searchTerm || typeof searchSuggestionsHandler !== 'function' || window.Cypress) {
            return;
        }
        searchSuggestionsHandler(searchTerm, columnName, suggestionLimit).then((res) => {
            const suggestions = res?.responseStatus?.code === 200 ? res.response.data : [];
            setBatteryList(suggestions);
            // Lets a form decide whether what the user typed matched anything, without
            // duplicating the fetch in the parent.
            if (onSuggestionsChange) onSuggestionsChange(suggestions, searchTerm);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchTerm, disableSuggestions]);

    useEffect(() => {
        if (batteryID === "") {
            setTypedValue("");
            setErrorTextField(false);
        }
    }, [batteryID]);

    useEffect(
        () => () => {
            setBatteryList([]);
            setTypedValue("");
            setErrorTextField(false);
        },
        []
    );

    const handleOpen = (value) => {
        if ((value || "").length >= minCharsToOpen) {
            setOpen(true);
        }
    };

    function keyPress(e) {
        if (e.keyCode === 13) {
            // A form field must not treat Enter as "commit and clear" — the value is already
            // in form state via onValueChange, and clearing it would discard what was typed.
            if (isControlled) {
                setOpen(false);
                return;
            }
            if (!typedValue.trim()) {
                if (onExecuteSearch) onExecuteSearch();
                return;
            }
            onSelect(typedValue.trim());
            setTypedValue("");
            setErrorTextField(false);
            setOpen(false);
        }
    }

    return (
        <>
            <Autocomplete
                filterSelectedOptions
                disableClearable
                freeSolo
                disablePortal
                id="combo-box-demo"
                options={batteryList.filter((item, i, ar) => ar.indexOf(item) === i)}
                open={open}
                onOpen={handleOpen}
                onClose={() => setOpen(false)}
                slotProps={{
                    listbox: {
                        sx: { fontSize: "14px" },
                    },
                }}
                // clearOnBlur would wipe a form field's value the moment it loses focus.
                clearOnBlur={!isControlled}
                value={batteryID}
                inputValue={searchTerm}
                onChange={(event, selected, reason) => {
                    // reason === "createOption" means MUI committed the raw INPUT TEXT,
                    // not a chosen option -- it is what fires when Enter is pressed on
                    // free text. Our keyPress above returns early for a controlled field
                    // but does not set event.defaultMuiPrevented, so MUI's own keydown
                    // handler runs on and reaches its freeSolo branch
                    // (useAutocomplete: `selectNewValue(event, inputValue,
                    // 'createOption', 'freeSolo')`).
                    //
                    // Treating that as a selection is wrong for a form field, and for the
                    // serial fields it is a correctness bug: onSelect marks the value as
                    // registry-sourced, which exempts it from the format rule. Typing a
                    // malformed serial and pressing Enter cleared the error and let it
                    // submit.
                    //
                    // Filtered on the reason rather than by swallowing Enter, so that
                    // Enter on a HIGHLIGHTED option (reason "selectOption") still selects
                    // it -- keyboard users would otherwise lose the dropdown entirely.
                    if (isControlled && reason === "createOption") {
                        setOpen(false);
                        return;
                    }
                    onSelect(selected);
                    // Search box: clear so the next term can be typed. Form field: keep it.
                    if (!isControlled) setTypedValue("");
                    setOpen(false);
                }}
                onKeyDown={keyPress}
                onInputChange={(event, value, reason) => {
                    // 'reset' is MUI syncing inputValue after a selection; forwarding it would
                    // echo the value back to the parent and re-trigger the suggestion fetch.
                    if (isControlled && reason === "reset") return;
                    if (isControlled) {
                        if (onValueChange) onValueChange(value);
                    } else {
                        setTypedValue(value);
                    }
                    setErrorTextField(false);
                    handleOpen(value);
                }}
                getOptionLabel={(option) => option}
                forcePopupIcon={false}
                size="small"
                sx={{ width: "100%", p: 0 }}
                PopperProps={{
                    container: container || undefined,
                }}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        error={errorTextfield || error}
                        placeholder={props.placeholder ? props.placeholder : "Search batteries"}
                        autoFocus={autoFocus}
                        inputProps={{ ...params.inputProps, ...(inputProps || {}) }}
                        // The search-box styling strips the border because SearchInputWrapper
                        // draws it. A form field owns its own border, so it passes inputSx.
                        sx={inputSx || {
                            width: "100%",
                            paddingLeft: '0px !important',
                            "& fieldset": { border: "none" },

                            "& .MuiInputBase-input": {
                                fontSize: "14px !important",
                                caretColor: "#5D5D5D",
                                paddingLeft: '0px !important',
                                "&::placeholder": {
                                    fontWight: 400,
                                    fontSize: "14px",
                                    color: "#ADB5BE",
                                    opacity: 1,
                                },
                            }
                        }}
                    />
                )}
            />
        </>
    );
}
